import React, {useState, useEffect} from 'react';
import {Button, Confirm, Modal, Spinner} from "@tag/tag-components-react-v4";
import {CustomerDTO} from "../types/CustomerDTO";
import Reset24hFileModal from "./Reset24hFileModal";
import AcpSettingsService from "../services/AcpSettingsService";
import {Moment} from "moment";
import DataExportModal from "./DataExportModal";
import styles from './CustomerMenuModal.module.scss'
import CustomerService from "../services/CustomerService";
import AppRolesService from "../../General/services/AppRolesService";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import RestoreLiveToTestModal from "./RestoreLiveToTestModal";
import MoveCustomerToAppGroupModal from "./MoveCustomerToAppGroupModal";
import RemoveCustomerModal from "./RemoveCustomerModal";
import { DisablePeoplePlannerModal } from './DisablePeoplePlannerModal';
import { DisableAppInsideAcrModal } from './DisableAppInsideAcrModal';
import { EnableCareRosteringModal } from './EnableCareRosteringModal/EnableCareRosteringModal';
import { CareConnectConfigurationModal } from './CareConnectConfigurationModal/CareConnectConfigurationModal';
import RestoreToSupportServerModal from './RestoreToSupportServerModal';
import RetrieveDbBackupsModal from './RetrieveDbBackupsModal';
import UnlinkWorkspaceModal from './UnlinkWorkspaceModal';
import { ProjectAccents } from "../../General/config/projectAccents";

function CustomerMenuModal(props: {
    selectedCustomer: CustomerDTO, 
    handleClose: Function, 
    setAlert: Function, 
    reloadCustomerData: Function,
    updateCustomerCareRosteringStatus?: (clientId: number, isEnabled: boolean) => Promise<void>
}) {

    const [reset24hFileVisible, setReset24hFileVisible] = useState(false);
    const [dataExportVisible, setDataExportVisible] = useState(false);
    const [removeModalVisible, setRemoveModalVisible] = useState(false);
    const [restoreLiveToTestVisible, setRestoreLiveToTestVisible] = useState(false);
    const [moveAppGrpVisible, setMoveAppGrpVisible] = useState(false);
    const [disablePeoplePlannerVisible, setDisablePeoplePlannerVisible] = useState(false);
    const [disableAppInsideAcrVisible, setDisableAppInsideAcrVisible] = useState(false);
    const [enableCareRosteringVisible, setEnableCareRosteringVisible] = useState(false);
    const [careConnectConfigurationVisible, setCareConnectConfigurationVisible] = useState(false);
    const [restoreToSupportServerVisible, setRestoreToSupportServerVisible] = useState(false);
    const [restoreToSupportServerAvailable, setRestoreToSupportServerAvailable] = useState(false);
    const [retrieveDbBackupsVisible, setRetrieveDbBackupsVisible] = useState(false);
    const [retrieveDbBackupsAvailable, setRetrieveDbBackupsAvailable] = useState(false);
    const [isCchCustomer, setIsCchCustomer] = useState(false);
    const [resetServiceBrokerConfirmVisible, setResetServiceBrokerConfirmVisible] = useState(false);
    const [unlinkWorkspaceVisible, setUnlinkWorkspaceVisible] = useState(false);
    const [employeeFilterConfirmVisible, setEmployeeFilterConfirmVisible] = useState(false);
    const [serviceLocationFilterConfirmVisible, setServiceLocationFilterConfirmVisible] = useState(false);
    const [loading, setLoading] = useState<string|boolean>(false);
    const [menuLoading, setMenuLoading] = useState(true);
    
    const { accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    let canDeleteEnv = AppRolesService.canUserDeleteEnvironments(accounts, isAuthenticated);
    let canLockEnv = AppRolesService.canUserLockAppEnvironments(accounts, isAuthenticated);
    let isAdmin = AppRolesService.isUserAdmin(accounts, isAuthenticated);
    let canReactiveSupport = AppRolesService.isUserAPPReactiveSupport(accounts, isAuthenticated);
    let canConfigureACR = AppRolesService.canUserConfigureACR(accounts, isAuthenticated);
    let canEnableACR = AppRolesService.canUserEnableACR(accounts, isAuthenticated);
    let canRestoreToSupportServer = AppRolesService.canUserRestoreToSupportServer(accounts, isAuthenticated);
    let canRetrieveDbBackups = AppRolesService.canUserRetrieveDbBackups(accounts, isAuthenticated);
    let canUnlinkWorkspace = AppRolesService.canUserUnlinkWorkspace(accounts, isAuthenticated);

    const delay = (ms:number) => new Promise(res => setTimeout(res, ms));

    useEffect(() => {
        if (!canRetrieveDbBackups && !canRestoreToSupportServer) {
            setMenuLoading(false);
            return;
        }

        setMenuLoading(true);

        Promise.all([
            CustomerService.getRestoreToSupportServerAvailable(props.selectedCustomer.environmentName)
                .then(setRestoreToSupportServerAvailable)
                .catch(() => setRestoreToSupportServerAvailable(false)),
            CustomerService.getRetrieveDbBackupsAvailable(props.selectedCustomer.environmentName)
                .then(setRetrieveDbBackupsAvailable)
                .catch(() => setRetrieveDbBackupsAvailable(false)),
            CustomerService.getIsCchCustomer(props.selectedCustomer.clientId, props.selectedCustomer.environmentName)
                .then(setIsCchCustomer)
                .catch(() => setIsCchCustomer(false))
        ]).finally(() => setMenuLoading(false));
    }, [canRetrieveDbBackups, canRestoreToSupportServer, props.selectedCustomer.clientId, props.selectedCustomer.environmentName]);

    const redirectToCustomersAPI = () => {
        window.open(`https://${props.selectedCustomer.serviceName}-${props.selectedCustomer.appgrp}.peopleplanner.biz/apidocumentation/#`, '_blank');
    }
    
    const updateACPSettings = (clientId: number, settingId: number, dateTimeToday: Moment) => {
        AcpSettingsService.updateAcp24HFileForCustomer(clientId, settingId, dateTimeToday, props.selectedCustomer.environmentName)
            .then(() => {
                props.setAlert({type: 'success', message: 'ACP Settings Updated'});
            })
            .catch(() => {
                props.setAlert({type: 'error', message: 'An error has occured, please contact HSC Care DevOps'});
            })
            .then(() => {
                props.handleClose();
                props.reloadCustomerData();
            });
    }
    
    const deleteAPPCustomer = () => {
        if (!canDeleteEnv) {
            throw new Error()
        }
        
        setLoading(true);
        
        CustomerService.deleteCustomer(props.selectedCustomer.clientId, props.selectedCustomer.environmentName)
            .then((result) => {
                props.setAlert({type: 'success', message: 'Customer Deleted'});
            })
            .catch(() => {
                props.setAlert({type: 'error', message: 'An error has occured, please contact HSC Care DevOps'});
            })
            .then(() => {
                setLoading(false)
                props.handleClose();
                props.reloadCustomerData();
            });
    }

    const handleResetServiceBroker = async () => {
        setResetServiceBrokerConfirmVisible(false);
        setLoading('Resetting Service Broker...');
        try {
            await CustomerService.resetServiceBroker(props.selectedCustomer.clientId, props.selectedCustomer.environmentName);
            props.setAlert({type: 'success', message: 'Service Broker reset successfully'});
        } catch {
            props.setAlert({type: 'error', message: 'An error has occurred, please contact HSC Care DevOps'});
        } finally {
            setLoading(false);
            props.handleClose();
        }
    };

    const handleRunEmployeeFilters = async () => {
        setEmployeeFilterConfirmVisible(false);
        setLoading('Running Employee Filters...');
        try {
            await AcpSettingsService.runEmployeeFiltersForCCH(props.selectedCustomer.clientId, props.selectedCustomer.environmentName);
            props.setAlert({type: 'success', message: 'Employee Filters applied successfully'});
        } catch {
            props.setAlert({type: 'error', message: 'An error has occurred, please contact HSC Care DevOps'});
        } finally {
            setLoading(false);
            props.handleClose();
        }
    };

    const handleRunServiceLocationFilters = async () => {
        setServiceLocationFilterConfirmVisible(false);
        setLoading('Running Service Location Filters...');
        try {
            await AcpSettingsService.runServiceLocationFiltersForCCH(props.selectedCustomer.clientId, props.selectedCustomer.environmentName);
            props.setAlert({type: 'success', message: 'Service Location Filters applied successfully'});
        } catch {
            props.setAlert({type: 'error', message: 'An error has occurred, please contact HSC Care DevOps'});
        } finally {
            setLoading(false);
            props.handleClose();
        }
    };

    const handleLockAPPCustomer = async (salesForceId: string) => {
        let errorMessage = '';
        setLoading('Starting...');

        try {
            const requestLockResponse = await CustomerService.requestlockAppCustomer(props.selectedCustomer.clientId, salesForceId, props.selectedCustomer.environmentName);
            
            const taskId = requestLockResponse.taskId;

            let status;
            do {
                status = await CustomerService.lockAppCustomerStatus(taskId);
                setLoading(status.progressMessage);
                await delay(50);
            } while (!status.complete && !status.errorMessage);
            
            if (status.errorMessage) {
                errorMessage = status.errorMessage;
                throw new Error();
            }

            props.setAlert({type: 'success', message: 'Customer Locked - Please view terminations page'});
        } catch (e) {
            props.setAlert({type: 'error', message: errorMessage || 'An error has occured, please contact HSC Care DevOps'});
        } finally {
            props.handleClose();
            props.reloadCustomerData();
            setLoading(false);
        }
    }
    
    let content;
    if (loading || menuLoading) {
        content = <Spinner full={true} device="touch">{loading}</Spinner>;
    }
    else {
        content = <>
            <Modal
                visible={!!props.selectedCustomer}
                accent={ProjectAccents.PeoplePlanner}
                heading={props.selectedCustomer?.name + ' - (' + props.selectedCustomer?.clientId + ')'}
                primaryButtonProps={{buttonStyle: {'display': 'none'}}}
                onCancelButtonClick={() => {
                    props.handleClose();
                }}
                onClose={() => props.handleClose()}
            >
                <div className={styles.CustomerMenuModalButtonContainer}>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Reset ACP 24h file'} onClick={() => setReset24hFileVisible(true)} />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Data Export'} onClick={() => setDataExportVisible(true)} />
                    </div>
                    <div
                        className={styles.CustomerMenuModalButtonElement}
                        title={!canRestoreToSupportServer
                            ? 'You need the Administrator, HSC Support or App Reactive Support role to use this feature'
                            : isCchCustomer
                                ? 'This feature is not yet available for CCH customers'
                                : !restoreToSupportServerAvailable
                                    ? 'Restore to Support Server is not available for this corporate site'
                                    : undefined}
                    >
                        <Button
                            text={"Restore to Support Server"}
                            disabled={!canRestoreToSupportServer || !restoreToSupportServerAvailable || isCchCustomer}
                            onClick={() => setRestoreToSupportServerVisible(true)}
                        />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Go To API'} onClick={() => redirectToCustomersAPI()} />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Remove Tenant'} onClick={() => setRemoveModalVisible(true)} />
                    </div>
                    <hr className={styles.CustomerMenuModalSeparator} />
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button
                            text={"Remove APP Option from EVO"}
                            disabled={!canConfigureACR}
                            onClick={() => setDisablePeoplePlannerVisible(true)}
                        />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button
                            text={"Remove APP Button from ACR"}
                            disabled={!canConfigureACR}
                            onClick={() => setDisableAppInsideAcrVisible(true)}
                        />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button
                            text={"Enable Care Rostering"}
                            disabled={!canEnableACR}
                            onClick={() => setEnableCareRosteringVisible(true)}
                        />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button
                            text={"Configure Care Connect"}
                            disabled={!canEnableACR}
                            onClick={() => setCareConnectConfigurationVisible(true)}
                        />
                    </div>
                    <hr className={styles.CustomerMenuModalSeparator} />
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button
                            text={'Employee Filters - CCH ONLY'}
                            disabled={!canReactiveSupport}
                            onClick={() => setEmployeeFilterConfirmVisible(true)}
                        />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button
                            text={'Service Location Filters - CCH ONLY'}
                            disabled={!canReactiveSupport}
                            onClick={() => setServiceLocationFilterConfirmVisible(true)}
                        />
                    </div>
                    <div
                        className={styles.CustomerMenuModalButtonElement}
                        title={!canRetrieveDbBackups
                            ? 'You need the Administrator or App Reactive Support role to use this feature'
                            : isCchCustomer
                                ? 'This feature is not yet available for CCH customers'
                                : !retrieveDbBackupsAvailable
                                    ? 'Retrieve DB backups is not available for this corporate site'
                                    : undefined}
                    >
                        <Button
                            text={"Retrieve DB backups"}
                            disabled={!canRetrieveDbBackups || !retrieveDbBackupsAvailable || isCchCustomer}
                            onClick={() => setRetrieveDbBackupsVisible(true)}
                        />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Restore Live to Test'} disabled={!canReactiveSupport} onClick={() => setRestoreLiveToTestVisible(true)} />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Move'} disabled={!isAdmin} onClick={() => setMoveAppGrpVisible(true)} />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Reset Service Broker'} disabled={!canReactiveSupport} onClick={() => setResetServiceBrokerConfirmVisible(true)} />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Unlink Workspace'} disabled={!canUnlinkWorkspace} onClick={() => setUnlinkWorkspaceVisible(true)} />
                    </div>
                </div>
            </Modal>
        </>
    }

    return (
        <>
            {content}
            {reset24hFileVisible ?
                <Reset24hFileModal
                    visible={reset24hFileVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setReset24hFileVisible(false)}
                    updateACPSettings={(clientId: number, settingId: number, dateTimeToday: Moment) =>
                        updateACPSettings(clientId, settingId, dateTimeToday)
                    }
                /> : undefined
            }
            {dataExportVisible ?
                <DataExportModal
                    visible={dataExportVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setDataExportVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                    setAlert={props.setAlert}
                /> : undefined
            }
            {unlinkWorkspaceVisible ?
                <UnlinkWorkspaceModal
                    visible={unlinkWorkspaceVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setUnlinkWorkspaceVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                    setAlert={props.setAlert}
                /> : undefined
            }
            {removeModalVisible ?
                <RemoveCustomerModal
                    visible={removeModalVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setRemoveModalVisible(false)}
                    handleDelete={deleteAPPCustomer}
                    handleLock={handleLockAPPCustomer}
                    canDeleteEnv={canDeleteEnv}
                    canLockEnv={canLockEnv}
                /> : undefined
            }
            {restoreLiveToTestVisible ?
                <RestoreLiveToTestModal
                    visible={restoreLiveToTestVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setRestoreLiveToTestVisible(false)}
                    setLoading={setLoading}
                    setAlert={props.setAlert}
                    handleCustomerMenuModalClose={props.handleClose}
                /> : undefined
            }
            {moveAppGrpVisible ?
                <MoveCustomerToAppGroupModal
                    visible={moveAppGrpVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setMoveAppGrpVisible(false)}
                    setLoading={setLoading}
                    setAlert={props.setAlert}
                    handleCustomerMenuModalClose={props.handleClose}
                    reloadCustomerList={props.reloadCustomerData}
                /> : undefined
            }
            {disablePeoplePlannerVisible ?
                <DisablePeoplePlannerModal
                    visible={disablePeoplePlannerVisible}
                    selectedCustomer={props.selectedCustomer}
                    setAlert={props.setAlert}
                    handleClose={() => setDisablePeoplePlannerVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                /> : undefined
            }
            {disableAppInsideAcrVisible ?
                <DisableAppInsideAcrModal
                    visible={disableAppInsideAcrVisible}
                    selectedCustomer={props.selectedCustomer}
                    setAlert={props.setAlert}
                    handleClose={() => setDisableAppInsideAcrVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                /> : undefined
            }
            {enableCareRosteringVisible ?
                <EnableCareRosteringModal
                    visible={enableCareRosteringVisible}
                    selectedCustomer={props.selectedCustomer}
                    setAlert={props.setAlert}
                    handleClose={() => setEnableCareRosteringVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                    updateCustomerCareRosteringStatus={props.updateCustomerCareRosteringStatus}
                /> : undefined
            }
            {careConnectConfigurationVisible ?
                <CareConnectConfigurationModal
                    visible={careConnectConfigurationVisible}
                    selectedCustomer={props.selectedCustomer}
                    setAlert={props.setAlert}
                    handleClose={() => setCareConnectConfigurationVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                /> : undefined
            }
            {restoreToSupportServerVisible ?
                <RestoreToSupportServerModal
                    visible={restoreToSupportServerVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setRestoreToSupportServerVisible(false)}
                    setAlert={props.setAlert}
                    handleCustomerMenuModalClose={props.handleClose}
                /> : undefined
            }
            {retrieveDbBackupsVisible ?
                <RetrieveDbBackupsModal
                    visible={retrieveDbBackupsVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setRetrieveDbBackupsVisible(false)}
                    setAlert={props.setAlert}
                    handleCustomerMenuModalClose={props.handleClose}
                /> : undefined
            }
            {resetServiceBrokerConfirmVisible ?
                <Confirm
                    visible={resetServiceBrokerConfirmVisible}
                    type="warning"
                    accent={ProjectAccents.PeoplePlanner}
                    heading={'Are you sure?'}
                    onPrimaryButtonClick={handleResetServiceBroker}
                    onCancelButtonClick={() => setResetServiceBrokerConfirmVisible(false)}
                    onClose={() => setResetServiceBrokerConfirmVisible(false)}
                >
                    <div style={{lineHeight: '1.6'}}>
                        <p><strong>This will reset the Service Broker on the main database.</strong></p>
                        <p>The database will be removed from High Availability and the Service Broker will be enabled (ENABLE_BROKER) with rollback immediate.</p>
                    </div>
                </Confirm> : undefined
            }
            {employeeFilterConfirmVisible ?
                <Confirm
                    visible={employeeFilterConfirmVisible}
                    type="warning"
                    accent={ProjectAccents.PeoplePlanner}
                    heading={'Are you sure?'}
                    onPrimaryButtonClick={handleRunEmployeeFilters}
                    onCancelButtonClick={() => setEmployeeFilterConfirmVisible(false)}
                    onClose={() => setEmployeeFilterConfirmVisible(false)}
                >
                    <div style={{lineHeight: '1.6'}}>
                        <p><strong>This will modify database records for CCH only.</strong></p>
                        <p>For every AccessCarePlanningSettings entry, any missing BranchArea rows will be inserted into <strong>AccessCarePlanningEmployeeFilter</strong>.</p>
                        <p>This action cannot be easily undone. Do not run this for non-CCH clients.</p>
                    </div>
                </Confirm> : undefined
            }
            {serviceLocationFilterConfirmVisible ?
                <Confirm
                    visible={serviceLocationFilterConfirmVisible}
                    type="warning"
                    accent={ProjectAccents.PeoplePlanner}
                    heading={'Are you sure?'}
                    onPrimaryButtonClick={handleRunServiceLocationFilters}
                    onCancelButtonClick={() => setServiceLocationFilterConfirmVisible(false)}
                    onClose={() => setServiceLocationFilterConfirmVisible(false)}
                >
                    <div style={{lineHeight: '1.6'}}>
                        <p><strong>This will modify database records for CCH only.</strong></p>
                        <p>For every AccessCarePlanningSettings entry matching 'mobizio' or 'acp', any missing BranchArea rows will be inserted into <strong>AccessCarePlanningServiceLocationFilter</strong>.</p>
                        <p>This action cannot be easily undone. Do not run this for non-CCH clients.</p>
                    </div>
                </Confirm> : undefined
            }
        </>
    )

}

export default CustomerMenuModal;
